import { useCallback, useEffect, useRef, useState } from 'react';

import AssistantScriptsService from '@shared/services/AssistantScriptsService';
import type { IAssistantScript } from '@shared/interfaces/assistantScript';

export interface UseAllAssistantScriptsResult {
  scripts: IAssistantScript[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

// AssistantProvider's `scripts` are filtered by current mapName + brandingId
// (see useAssistantScripts.ts:272-282), which hides scripts that exist on
// other brandings — including ones the editor needs to display.
// This hook loads the full unfiltered list so the editor tree shows every
// script the user has authored, regardless of scope.
export const useAllAssistantScripts = (): UseAllAssistantScriptsResult => {
  const [scripts, setScripts] = useState<IAssistantScript[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const mountedRef = useRef(true);

  const fetchOnce = useCallback(async () => {
    try {
      const data = await AssistantScriptsService.getAssistantScripts();
      if (!mountedRef.current) {
        return;
      }
      setScripts(data);
      setError(null);
    } catch (fetchError) {
      if (!mountedRef.current) {
        return;
      }
      console.error(
        `Failed to load assistant scripts: ${(fetchError as Error).message}`,
      );
      setError(fetchError as Error);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    setLoading(true);
    void fetchOnce();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchOnce]);

  return { scripts, loading, error, refetch: fetchOnce };
};
